// 月パス（CONFIG.monthly）。買い切りの30日（自動では更新しない）。期間は Profile.monthlyUntil（期限）だけで表す。
// 期間中：兵糧の自然回復が速い（stamina.ts）、チャットのアイコン縁が選べる（期限が切れたら縁なしに戻す）。
// 姿＋10種は初めて買ったときから永続（権利 PREMIUM_AVATARS_KEY。2026-11-02 から。それ以前に買った人は monthlyUntil があれば持っている扱い）
import { chatFrame, isChatFrame } from '../data/chat-frames';
import { DEFAULT_PLAYER_AVATAR, isPremiumAvatar } from '../data/player-avatars';
import { CONFIG } from './config';
import type { OpResult, Profile } from './profile';

export const monthlyActive = (p: Pick<Profile, 'monthlyUntil'>, now: number) => (p.monthlyUntil ?? 0) > now;

/** 月パスの姿＋10種の権利（初回の購入で付く。買い切り） */
export const PREMIUM_AVATARS_KEY = 'avatars:premium';
/** 月パスの姿＋10種を使えるか（一度でも月パスを買った） */
export const hasPremiumAvatars = (p: Pick<Profile, 'monthlyUntil'> & { entitlements?: string[] }) => !!p.entitlements?.includes(PREMIUM_AVATARS_KEY) || (p.monthlyUntil ?? 0) > 0;

const SPAN = CONFIG.monthly.days * 86_400_000;

/** 購入1回：今の期限（切れていれば今）から30日延ばす。付いてくる10連はサーバーがその場で引く（pullPaidGacha。券は付けない） */
export function grantMonthly(p: Profile, now: number) {
  p.monthlyUntil = Math.max(p.monthlyUntil ?? 0, now) + SPAN;
  if (!p.entitlements.includes(PREMIUM_AVATARS_KEY)) p.entitlements.push(PREMIUM_AVATARS_KEY);
}

/** 買えるか：残りが buyWithinDays 日以上あるときは買えない（買い足しは残り10日未満から） */
export const monthlyCanBuy = (p: Pick<Profile, 'monthlyUntil'>, now: number) => (p.monthlyUntil ?? 0) - now < CONFIG.monthly.buyWithinDays * 86_400_000;

/** 返金・チャージバック：その1回ぶんの30日を縮める（今より前にはしない）。引いた10連の中身は戻さない（消耗品と同じ） */
export function revokeMonthly(p: Profile, now: number) {
  if ((p.monthlyUntil ?? 0) > now) p.monthlyUntil = Math.max(now, p.monthlyUntil! - SPAN);
}

/** 期限が remindDays 日以内なら残りの日数（切り上げ）。まだ先・切れている・買っていないなら null */
export function monthlyEndingDays(p: Pick<Profile, 'monthlyUntil'>, now: number): number | null {
  const left = (p.monthlyUntil ?? 0) - now;
  if (left <= 0 || left > CONFIG.monthly.remindDays * 86_400_000) return null;
  return Math.ceil(left / 86_400_000);
}

/** 他の人に見せる姿（月パスの姿は一度買えば期限が切れても使える） */
export function shownAvatar(p: Pick<Profile, 'monthlyUntil' | 'settings'> & { entitlements?: string[] }, _now: number): string | null {
  const id = p.settings?.avatar ?? null;
  return isPremiumAvatar(id) && !hasPremiumAvatars(p) ? DEFAULT_PLAYER_AVATAR : id;
}

/** 他の人に見せるアイコン縁（期間中だけ） */
export function shownFrame(p: Pick<Profile, 'monthlyUntil' | 'settings'>, now: number): string | null {
  const id = p.settings?.frame;
  return monthlyActive(p, now) && chatFrame(id) ? id! : null;
}

export function setFrame(p: Profile, id: string | null, now: number): OpResult<{ frame: string | null }> {
  if (id !== null && !isChatFrame(id)) return { ok: false, error: 'frame' };
  if (id !== null && !monthlyActive(p, now)) return { ok: false, error: 'monthly_required' };
  const s = { ...p.settings };
  if (id) s.frame = id;
  else delete s.frame;
  p.settings = s;
  return { ok: true, frame: id };
}
